import { useBreakpoint } from '@chakra-ui/react'
import {
  faBug,
  faBullhorn,
  faChalkboard,
  faFileCertificate,
  faFlagAlt,
  faGear,
  faGraduationCap,
  faLock,
  faMessageQuestion,
  faSignOut,
  faUser,
  faUserHeadset,
} from '@fortawesome/pro-regular-svg-icons'
import { useMemo } from 'react'

import { isNotNull } from '@/utils/helpers'

import { useUser } from '@/client/context/user'
import * as url from '@/client/services/url'
import { hasUserRole, isUserSuspended } from '@/client/services/user'
import hasUserAuthScope from '@/client/services/user/hasUserAuthScope'
import { showIntercomMessenger } from '@/client/vendor/intercom'

import type { MenuItemType } from '@/components/Worlds/UI/Menu/types'

export default function useMenuItemGroups(): MenuItemType[][] {
  const user = useUser()

  const userIsSuspended = isUserSuspended(user)
  const hasAdminAccess = hasUserRole(user, 'admin:basic')

  const breakpoint = useBreakpoint()
  const isMobile = useMemo(() => breakpoint === 'base', [breakpoint])

  const myTutoringItem: MenuItemType[] =
    isMobile && hasUserAuthScope(user, 'world:sessions:host', null)
      ? [
          {
            label: 'My Tutoring',
            href: url.getUrl('tutor'),
            icon: faGraduationCap,
          },
        ]
      : []

  return [
    [
      ...myTutoringItem,
      userIsSuspended
        ? null
        : {
            label: 'My Profile',
            icon: faUser,
            href: url.getUserUrl({ userId: user.id, username: user.username }),
          },
      {
        label: 'My Portfolio',
        icon: faFileCertificate,
        href: url.getTranscriptUrlByUUID(user.uuidTranscript),
      },
      {
        label: 'My Classrooms',
        icon: faChalkboard,
        href: url.getClassroomsUrl(),
      },
      {
        label: 'Settings',
        icon: faGear,
        href: url.getSettingsUrl(),
      },
    ].filter(isNotNull),
    [
      {
        label: 'Refer a Friend',
        icon: faBullhorn,
        href: url.getReferUrl(),
      },
      {
        label: 'Report a Bug',
        icon: faBug,
        href: url.getCannyBugReportUrl(),
      },
      {
        label: 'Help Desk',
        icon: faMessageQuestion,
        href: url.faq.getUrl(),
      },
      {
        label: 'Chat with Support',
        icon: faUserHeadset,
        onClick: showIntercomMessenger,
      },
      {
        label: 'Share Safety Concern',
        icon: faFlagAlt,
        href: url.getReportUrl(),
      },
    ],
    hasAdminAccess
      ? [
          {
            label: 'Admin',
            href: url.getUrl('admin'),
            icon: faLock,
          },
        ]
      : null,
    [
      {
        label: 'Log Out',
        icon: faSignOut,
        href: url.getLogoutUrl(),
        isExternal: false,
        color: 'fg.error',
      },
    ],
  ].filter(isNotNull)
}
